import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronRight, Landmark, Scale, TrendingUp, RefreshCcw } from "lucide-react";
import Marquee from "@/components/Marquee";
import Seo from "@/components/Seo";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1779127524787-2fbb70d483d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtaWxhbiUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3OTE3OTMwNTB8MA&ixlib=rb-4.1.0&q=85&fit=crop&w=2400&h=1200";

const CONTENT = {
  it: {
    seoTitle: "Mediolanum Consulting Srl | Consulenza Societaria e Fiscale — Milano",
    seoDesc: "Mediolanum Consulting Srl: consulenza societaria, fiscale e operazioni straordinarie a Milano. M&A, pianificazione fiscale, governance, ristrutturazioni.",
    heroLines: [
      { text: "Strategia societaria,", gold: false },
      { text: "fiscalità e operazioni", gold: false },
      { text: "straordinarie.", gold: true },
    ],
    sub: "Affianchiamo imprenditori e società nelle decisioni che contano: architetture societarie, strategia fiscale e operazioni straordinarie, con il rigore del metodo milanese.",
    cta1: "Richiedi una Consulenza",
    cta2: "Scopri i Servizi",
    stats: [
      { value: "2025", label: "Costituzione a Milano" },
      { value: "3", label: "Aree di intervento" },
    ],
    servicesEyebrow: "Ambiti di Eccellenza",
    servicesCopy: "Ogni mandato è seguito da un team integrato di dottori commercialisti, avvocati e corporate finance advisor.",
    services: [
      {
        title: "Operazioni Straordinarie & M&A",
        copy: "Fusioni, acquisizioni, cessioni di ramo d'azienda e operazioni transfrontaliere. Dalla due diligence al closing, presidiamo ogni fase dell'operazione.",
      },
      {
        title: "Consulenza Fiscale & Tributaria",
        copy: "Pianificazione fiscale nazionale e internazionale, transfer pricing, ruling preventivi e assistenza in sede di verifica.",
      },
      {
        title: "Strategie Societarie & Governance",
        copy: "Architetture statutarie, patti parasociali, passaggio generazionale e modelli di governance ESG per organi amministrativi.",
      },
      {
        title: "Crisi d'Impresa & Ristrutturazioni",
        copy: "Piani di risanamento attestati ex art. 56 CCII, composizione negoziata e ristrutturazione del debito bancario ed erariale.",
      },
    ],
    metodoEyebrow: "Il Metodo Mediolanum",
    metodo: [
      { n: "01", title: "Audit & Due Diligence", copy: "Mappatura integrale del perimetro societario, fiscale e finanziario. Nessuna operazione inizia senza una fotografia esatta del rischio." },
      { n: "02", title: "Architettura dell'Operazione", copy: "Strutturazione giuridica e fiscale dell'operazione, con simulazioni di scenario e definizione della strategia negoziale." },
      { n: "03", title: "Esecuzione Tattica", copy: "Negoziazione, documentazione contrattuale e coordinamento di notai, banche e advisor fino al closing." },
      { n: "04", title: "Presidio Continuo", copy: "Monitoraggio post-closing, adempimenti societari e aggiornamento della strategia al mutare del contesto normativo." },
    ],
    insightsEyebrow: "News & Insights",
    ctaEyebrow: "Riservatezza Garantita",
    ctaCopy: "Un primo incontro conoscitivo, senza impegno, presso le nostre sedi di Milano o in videoconferenza riservata.",
    ctaBtn: "Prenota un Colloquio Riservato",
    learnMore: "Approfondisci",
    allArticles: "Tutti gli articoli",
  },
  en: {
    seoTitle: "Mediolanum Consulting Srl | Corporate & Tax Advisory — Milan",
    seoDesc: "Mediolanum Consulting Srl: corporate, tax and extraordinary transaction advisory in Milan. M&A, tax planning, governance, restructuring.",
    heroLines: [
      { text: "Corporate strategy,", gold: false },
      { text: "tax and extraordinary", gold: false },
      { text: "transactions.", gold: true },
    ],
    sub: "We advise entrepreneurs and companies on the decisions that matter: corporate structures, tax strategy and extraordinary transactions, with the rigour of the Milanese method.",
    cta1: "Request a Consultation",
    cta2: "Explore our Services",
    stats: [
      { value: "2025", label: "Founded in Milan" },
      { value: "3", label: "Practice areas" },
    ],
    servicesEyebrow: "Areas of Excellence",
    servicesCopy: "Every engagement is handled by an integrated team of chartered accountants, lawyers and corporate finance advisors.",
    services: [
      {
        title: "Extraordinary Transactions & M&A",
        copy: "Mergers, acquisitions, business unit disposals and cross-border transactions. From due diligence to closing, we oversee every phase of the deal.",
      },
      {
        title: "Tax & Fiscal Advisory",
        copy: "Domestic and international tax planning, transfer pricing, advance tax rulings and assistance during tax audits.",
      },
      {
        title: "Corporate Strategies & Governance",
        copy: "Articles-of-association architecture, shareholders' agreements, generational transition and ESG governance models for boards.",
      },
      {
        title: "Crisis & Restructuring",
        copy: "Certified recovery plans under art. 56 CCII, negotiated crisis composition and restructuring of bank and tax debt.",
      },
    ],
    metodoEyebrow: "The Mediolanum Method",
    metodo: [
      { n: "01", title: "Audit & Due Diligence", copy: "Full mapping of the corporate, tax and financial perimeter. No transaction begins without an exact picture of the risk." },
      { n: "02", title: "Transaction Architecture", copy: "Legal and tax structuring of the deal, with scenario simulations and definition of the negotiation strategy." },
      { n: "03", title: "Tactical Execution", copy: "Negotiation, contractual documentation and coordination of notaries, banks and advisors through to closing." },
      { n: "04", title: "Continuous Oversight", copy: "Post-closing monitoring, corporate compliance and strategy updates as the regulatory landscape evolves." },
    ],
    insightsEyebrow: "News & Insights",
    ctaEyebrow: "Confidentiality Guaranteed",
    ctaCopy: "A first introductory meeting, with no obligation, at our Milan offices or by confidential video conference.",
    ctaBtn: "Book a Confidential Meeting",
    learnMore: "Learn more",
    allArticles: "All articles",
  },
};

const SERVICE_ICONS = [TrendingUp, Landmark, Scale, RefreshCcw];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const [insights, setInsights] = useState([]);
  const { lang, t } = useLang();
  const c = CONTENT[lang];

  useEffect(() => {
    api.get("/insights").then((res) => setInsights(res.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Seo title={c.seoTitle} description={c.seoDesc} />
      <section ref={heroRef} className="relative flex min-h-[100svh] items-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Skyline di Milano" className="h-[120%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/75 via-navy-950/35 to-navy-950" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950/60 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-36 sm:px-8">
          <motion.p
            key={lang}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            Mediolanum Consulting Srl — Milano
          </motion.p>

          <h1 className="mt-8 font-serif text-4xl font-light leading-[1.06] tracking-tight text-slate-100 sm:text-5xl lg:text-7xl" data-testid="hero-headline">
            {c.heroLines.map((line, i) => (
              <span key={`${lang}-${i}`} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "text-gold-gradient italic" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subcopy"
          >
            {c.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contatti"
              data-testid="hero-cta-consulenza"
              className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-light"
            >
              {c.cta1}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/servizi"
              data-testid="hero-cta-servizi"
              className="rounded-full border border-white/25 px-7 py-3.5 text-sm text-slate-200 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              {c.cta2}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-20 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-white/10 pt-8"
            data-testid="hero-stats"
          >
            {c.stats.map((s) => (
              <div key={s.label}>
                <p className="font-serif text-3xl font-light text-gold-light sm:text-4xl">{s.value}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="services-bento-section">
        <SectionHeading
          eyebrow={c.servicesEyebrow}
          title={lang === "en"
            ? <>Four practices, <span className="text-gold-gradient italic">one single method.</span></>
            : <>Quattro pratiche, <span className="text-gold-gradient italic">un unico metodo.</span></>}
          copy={c.servicesCopy}
        />
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-8 lg:grid-cols-12">
          <Reveal className="md:col-span-8 lg:col-span-8" delay={0}>
            <div className="card-surface card-surface-hover group relative h-full overflow-hidden rounded-2xl p-8 sm:p-10" data-testid="service-card-ma">
              <TrendingUp className="text-gold" size={30} />
              <h3 className="mt-6 font-serif text-2xl text-slate-100 sm:text-3xl">{c.services[0].title}</h3>
              <p className="mt-4 max-w-lg text-sm font-light leading-relaxed text-slate-400 sm:text-base">{c.services[0].copy}</p>
              <Link to="/servizi" className="mt-8 inline-flex items-center gap-1.5 text-sm text-gold" data-testid="service-card-ma-link">
                {c.learnMore} <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
          <Reveal className="md:col-span-4 lg:col-span-4 lg:row-span-2" delay={0.1}>
            <div className="card-surface card-surface-hover h-full rounded-2xl p-8 sm:p-10" data-testid="service-card-fiscale">
              <Landmark className="text-gold" size={30} />
              <h3 className="mt-6 font-serif text-2xl text-slate-100">{c.services[1].title}</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-slate-400 sm:text-base">{c.services[1].copy}</p>
              <p className="mt-8 border-t border-white/10 pt-6 font-mono text-xs uppercase tracking-[0.2em] text-gold/70">
                IRES · IVA · Ruling · TP
              </p>
            </div>
          </Reveal>
          {c.services.slice(2).map((s, i) => {
            const Icon = SERVICE_ICONS[i + 2];
            return (
              <Reveal key={s.title} className="md:col-span-4 lg:col-span-4" delay={0.15 + i * 0.08}>
                <div className="card-surface card-surface-hover h-full rounded-2xl p-8" data-testid={`service-card-${i + 3}`}>
                  <Icon className="text-gold" size={26} />
                  <h3 className="mt-5 font-serif text-xl text-slate-100 sm:text-2xl">{s.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{s.copy}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/10 bg-navy-900/40 py-28" data-testid="metodo-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            eyebrow={c.metodoEyebrow}
            title={lang === "en"
              ? <>From diagnosis to closing, <span className="text-gold-gradient italic">no improvisation.</span></>
              : <>Dalla diagnosi al closing, <span className="text-gold-gradient italic">senza improvvisazione.</span></>}
          />
          <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {c.metodo.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <div className="relative border-l border-gold/25 pl-7" data-testid={`metodo-step-${step.n}`}>
                  <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rotate-45 bg-gold" />
                  <p className="font-mono text-sm tracking-widest text-gold">{step.n}</p>
                  <h3 className="mt-3 font-serif text-xl text-slate-100">{step.title}</h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">{step.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-28 sm:px-8" data-testid="insights-preview-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading
            eyebrow={c.insightsEyebrow}
            title={lang === "en"
              ? <>Analysis from the <span className="text-gold-gradient italic">tax and corporate frontline.</span></>
              : <>Analisi dal <span className="text-gold-gradient italic">fronte fiscale e societario.</span></>}
          />
          <Reveal delay={0.1}>
            <Link to="/insights" className="group flex items-center gap-2 text-sm text-gold" data-testid="insights-preview-all-link">
              {c.allArticles}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
          {insights.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.1}>
              <Link to="/insights" className="card-surface card-surface-hover group block h-full overflow-hidden rounded-2xl" data-testid={`insight-preview-${i}`}>
                {a.image_url && (
                  <div className="h-44 overflow-hidden">
                    <img src={a.image_url} alt={a.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                )}
                <div className="p-7">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold">{t(a.topic)} · {a.read_time}</p>
                  <h3 className="mt-3 font-serif text-xl leading-snug text-slate-100">{a.title}</h3>
                  <p className="mt-3 line-clamp-2 text-sm font-light text-slate-400">{a.excerpt}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-white/10" data-testid="cta-section">
        <div className="navy-mesh absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 py-28 text-center sm:px-8">
          <Reveal>
            <p className="eyebrow">{c.ctaEyebrow}</p>
            <h2 className="mx-auto mt-5 max-w-3xl font-serif text-3xl font-light text-slate-100 sm:text-4xl lg:text-5xl">
              {lang === "en"
                ? <>Your next transaction deserves a <span className="text-gold-gradient italic">strategy of excellence.</span></>
                : <>La vostra prossima operazione merita una <span className="text-gold-gradient italic">strategia d'eccellenza.</span></>}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base font-light text-slate-400">{c.ctaCopy}</p>
            <Link
              to="/contatti"
              data-testid="cta-section-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-light"
            >
              {c.ctaBtn} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
